/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* See LICENSE.md in the project root for license terms and full copyright notice.
*--------------------------------------------------------------------------------------------*/
import { combineReducers } from "redux";
import { packageRegistryApi } from "services/package-registry-service/PackageRegistryService";
import { sandboxApi } from "services/sandbox-api/api";
import { configureStore } from "@reduxjs/toolkit";
import activeSandbox from "./features/active-sandbox/sandboxSlice";
import actions from "./features/actions/actionsSlice";
import settings from "./features/settings/settingsSlice";
import auth from "./features/auth/authSlice";
import header from "./features/header/headerSlice";
import transpiler from "./features/transpiler/transpilerSlice";
import error from "./features/error/errorSlice";
import test from "./features/test/testSlice"
import { rtkQueryErrorLogger } from "./middleware/rtkQueryErrorLogger";

const rootReducer = combineReducers({
  actions,
  activeSandbox,
  settings,
  auth,
  error,
  header,
  transpiler,
  test,
  // Add the generated reducer as a specific top-level slice
  [packageRegistryApi.reducerPath]: packageRegistryApi.reducer,
  [sandboxApi.reducerPath]: sandboxApi.reducer,
});

export const store = configureStore({
  reducer: rootReducer,
  // Adding the api middleware enables caching, invalidation, polling,
  // and other useful features of `rtk-query`.
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(rtkQueryErrorLogger, packageRegistryApi.middleware, sandboxApi.middleware),
});

// @ts-ignore // expose to global variable for debugging
window.sandboxStore = store;

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof rootReducer>;

// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;
